import { memo } from 'react'
import {
  VStack,
  HStack,
  Box,
  Text,
  Heading,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Tag,
  SimpleGrid,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Icon,
  useColorModeValue,
  Spinner,
  OrderedList,
  ListItem,
} from '@chakra-ui/react'
import {
  LuBeaker,
  LuClipboardList,
  LuChartBar,
  LuImage,
  LuDna,
} from 'react-icons/lu'
import { useProjectSource, useProjectSources } from '../../../../api/api_hooks/project-sources'

interface ExplorationViewProps {
  projectId: string
  selectedSourceId?: string | null
}

export const ExplorationView = memo(function ExplorationView({
  projectId,
  selectedSourceId,
}: ExplorationViewProps) {
  const { data: sourcesData } = useProjectSources(projectId)
  const { data: sourceData, isLoading } = useProjectSource(selectedSourceId || undefined)

  const cardBg = useColorModeValue('white', 'gray.700')
  const borderColor = useColorModeValue('gray.200', 'gray.600')

  const source = sourceData?.source
  const allSources = sourcesData?.sources || []

  // Aggregate data from all sources if no specific source is selected
  const aggregateEntities = () => {
    if (!allSources.length) return null

    const aggregated = {
      protocols: [] as any[],
      methods: [] as any[],
      results: [] as any[],
      entities: {
        pathologies: [] as string[],
        models: [] as string[],
        molecules: [] as string[],
        cells: [] as string[],
      },
      figures: [] as any[],
      statistics: [] as any[],
    }

    allSources.forEach((s) => {
      if (s.extractedEntities) {
        if (s.extractedEntities.protocols) {
          aggregated.protocols.push(...s.extractedEntities.protocols)
        }
        if (s.extractedEntities.methods) {
          aggregated.methods.push(...s.extractedEntities.methods)
        }
        if (s.extractedEntities.results) {
          aggregated.results.push(...s.extractedEntities.results)
        }
        if (s.extractedEntities.entities) {
          Object.keys(aggregated.entities).forEach((key) => {
            const k = key as keyof typeof aggregated.entities
            const entityArray = s.extractedEntities?.entities?.[k]
            if (entityArray && Array.isArray(entityArray)) {
              aggregated.entities[k].push(...entityArray)
            }
          })
        }
        if (s.extractedEntities.figures) {
          aggregated.figures.push(...s.extractedEntities.figures)
        }
        if (s.extractedEntities.statistics) {
          aggregated.statistics.push(...s.extractedEntities.statistics)
        }
      }
    })

    // Deduplicate entities
    Object.keys(aggregated.entities).forEach((key) => {
      const k = key as keyof typeof aggregated.entities
      aggregated.entities[k] = Array.from(new Set(aggregated.entities[k]))
    })

    return aggregated
  }

  const displayData = selectedSourceId ? source?.extractedEntities : aggregateEntities()

  if (isLoading && selectedSourceId) {
    return (
      <Box p={8} textAlign="center">
        <Spinner />
        <Text mt={4} color="gray.500">
          Chargement...
        </Text>
      </Box>
    )
  }

  if (!displayData) {
    return (
      <Box p={8} textAlign="center">
        <Text color="gray.500">
          {selectedSourceId
            ? 'Aucune donnée extraite pour cette source'
            : 'Aucune source traitée dans ce projet'}
        </Text>
        <Text fontSize="sm" color="gray.400" mt={2}>
          {!selectedSourceId && 'Ajoutez des sources et attendez qu\'elles soient traitées'}
        </Text>
      </Box>
    )
  }

  return (
    <VStack spacing={4} align="stretch" overflow="auto" h="full" px={2}>
      {/* Header */}
      <Box>
        <Heading size="md">
          {selectedSourceId
            ? `Exploration: ${source?.title}`
            : `Vue d'ensemble du projet (${allSources.length} sources)`}
        </Heading>
        {selectedSourceId && source && (
          <Badge colorScheme="blue" mt={2}>
            {source.sourceType}
          </Badge>
        )}
      </Box>

      {/* Entities Overview */}
      {displayData.entities && (
        <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
          <CardHeader>
            <HStack>
              <Icon as={LuDna} />
              <Heading size="sm">Entités biologiques</Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            <SimpleGrid columns={2} spacing={4}>
              {Object.entries(displayData.entities).map(([key, values]) => (
                <Box key={key}>
                  <Text fontSize="xs" fontWeight="semibold" mb={2} textTransform="capitalize">
                    {key === 'pathologies' && 'Pathologies'}
                    {key === 'models' && 'Modèles biologiques'}
                    {key === 'molecules' && 'Molécules'}
                    {key === 'cells' && 'Types cellulaires'}
                  </Text>
                  <HStack spacing={2} flexWrap="wrap">
                    {values && values.length > 0 ? (
                      values.map((val: string, idx: number) => (
                        <Tag key={idx} size="sm" colorScheme="purple">
                          {val}
                        </Tag>
                      ))
                    ) : (
                      <Text fontSize="xs" color="gray.500">
                        Aucune
                      </Text>
                    )}
                  </HStack>
                </Box>
              ))}
            </SimpleGrid>
          </CardBody>
        </Card>
      )}

      {/* Protocols */}
      {displayData.protocols && displayData.protocols.length > 0 && (
        <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
          <CardHeader>
            <HStack>
              <Icon as={LuBeaker} />
              <Heading size="sm">Protocoles expérimentaux</Heading>
              <Badge>{displayData.protocols.length}</Badge>
            </HStack>
          </CardHeader>
          <CardBody>
            <Accordion allowMultiple>
              {displayData.protocols.map((protocol: any, idx: number) => (
                <AccordionItem key={idx}>
                  <h2>
                    <AccordionButton>
                      <Box flex="1" textAlign="left" fontWeight="medium">
                        {protocol.title || `Protocole ${idx + 1}`}
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                  </h2>
                  <AccordionPanel pb={4}>
                    {protocol.steps && (
                      <Box mb={4}>
                        <Text fontSize="sm" fontWeight="semibold" mb={2}>
                          Étapes:
                        </Text>
                        <OrderedList fontSize="sm" spacing={1}>
                          {protocol.steps.map((step: string, i: number) => (
                            <ListItem key={i}>{step}</ListItem>
                          ))}
                        </OrderedList>
                      </Box>
                    )}
                    {protocol.reagents && protocol.reagents.length > 0 && (
                      <Box mb={4}>
                        <Text fontSize="sm" fontWeight="semibold" mb={2}>
                          Réactifs:
                        </Text>
                        <HStack spacing={2} flexWrap="wrap">
                          {protocol.reagents.map((r: string, i: number) => (
                            <Tag key={i} size="sm" colorScheme="green">
                              {r}
                            </Tag>
                          ))}
                        </HStack>
                      </Box>
                    )}
                    {protocol.equipment && protocol.equipment.length > 0 && (
                      <Box>
                        <Text fontSize="sm" fontWeight="semibold" mb={2}>
                          Équipement:
                        </Text>
                        <HStack spacing={2} flexWrap="wrap">
                          {protocol.equipment.map((e: string, i: number) => (
                            <Tag key={i} size="sm" colorScheme="blue">
                              {e}
                            </Tag>
                          ))}
                        </HStack>
                      </Box>
                    )}
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          </CardBody>
        </Card>
      )}

      {/* Methods */}
      {displayData.methods && displayData.methods.length > 0 && (
        <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
          <CardHeader>
            <HStack>
              <Icon as={LuClipboardList} />
              <Heading size="sm">Méthodes</Heading>
              <Badge>{displayData.methods.length}</Badge>
            </HStack>
          </CardHeader>
          <CardBody>
            <VStack align="stretch" spacing={3}>
              {displayData.methods.map((method: any, idx: number) => (
                <Box
                  key={idx}
                  p={3}
                  borderWidth="1px"
                  borderColor={borderColor}
                  borderRadius="md"
                >
                  <HStack mb={2}>
                    <Badge colorScheme="cyan">{method.type || 'Méthode'}</Badge>
                    <Text fontWeight="semibold">{method.name}</Text>
                  </HStack>
                  {method.description && (
                    <Text fontSize="sm" color="gray.600">
                      {method.description}
                    </Text>
                  )}
                  {method.parameters && Object.keys(method.parameters).length > 0 && (
                    <Box mt={2}>
                      <Text fontSize="xs" fontWeight="semibold" mb={1}>
                        Paramètres:
                      </Text>
                      <HStack spacing={2} flexWrap="wrap">
                        {Object.entries(method.parameters).map(([key, value]: any, i) => (
                          <Tag key={i} size="sm">
                            {key}: {value}
                          </Tag>
                        ))}
                      </HStack>
                    </Box>
                  )}
                </Box>
              ))}
            </VStack>
          </CardBody>
        </Card>
      )}

      {/* Results & Statistics */}
      {displayData.results && displayData.results.length > 0 && (
        <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
          <CardHeader>
            <HStack>
              <Icon as={LuChartBar} />
              <Heading size="sm">Résultats & Statistiques</Heading>
              <Badge>{displayData.results.length}</Badge>
            </HStack>
          </CardHeader>
          <CardBody>
            <VStack align="stretch" spacing={2}>
              {displayData.results.map((result: any, idx: number) => (
                <Box
                  key={idx}
                  p={3}
                  borderWidth="1px"
                  borderColor={borderColor}
                  borderRadius="md"
                >
                  <HStack justify="space-between">
                    <Text fontSize="sm" fontWeight="medium">
                      {result.metric}
                    </Text>
                    <HStack>
                      <Badge colorScheme="orange">{result.value}</Badge>
                      {result.pValue && (
                        <Badge colorScheme="red">p = {result.pValue}</Badge>
                      )}
                    </HStack>
                  </HStack>
                  {result.context && (
                    <Text fontSize="xs" color="gray.600" mt={1}>
                      {result.context}
                    </Text>
                  )}
                </Box>
              ))}
            </VStack>
          </CardBody>
        </Card>
      )}

      {/* Figures */}
      {displayData.figures && displayData.figures.length > 0 && (
        <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
          <CardHeader>
            <HStack>
              <Icon as={LuImage} />
              <Heading size="sm">Figures & Tableaux</Heading>
              <Badge>{displayData.figures.length}</Badge>
            </HStack>
          </CardHeader>
          <CardBody>
            <VStack align="stretch" spacing={3}>
              {displayData.figures.map((figure: any, idx: number) => (
                <Box
                  key={idx}
                  p={3}
                  borderWidth="1px"
                  borderColor={borderColor}
                  borderRadius="md"
                >
                  <HStack mb={2}>
                    <Badge>{figure.type || 'Figure'}</Badge>
                    <Text fontWeight="semibold">
                      {figure.number && `Figure ${figure.number}`}
                    </Text>
                  </HStack>
                  {figure.caption && (
                    <Text fontSize="sm" color="gray.600">
                      {figure.caption}
                    </Text>
                  )}
                  {figure.description && (
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      {figure.description}
                    </Text>
                  )}
                </Box>
              ))}
            </VStack>
          </CardBody>
        </Card>
      )}
    </VStack>
  )
})
